import { useEffect, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { UUID_REGEX } from '@saas/shared';

import {
  type SystemModuleFormLayoutRow,
  useSystemModuleFormLayouts,
} from '@/components/documents/DocumentLayoutPicker';
import type { LayoutConfig } from '@/components/form/RecursiveFormRenderer';

/**
 * useDocumentTypeAndLayoutParams — cluster wyciągnięty z 6 stron detalu
 * dokumentów (T2.3, unifikacja-formatek — silnik formatek, pilot na klasach
 * gołych; pilot ekstrakcji: CommercialDocDetail, InternalDocDetail).
 *
 * Spina identyczne sub-bloki, kopiowane 1:1 na każdej stronie:
 *  - brak bramki uprawnienia na wybór widoku formularza — lista layoutów
 *    (`GET /system-modules/:slug/layouts`) jest dostępna dla KAŻDEGO zalogowanego
 *    usera tenanta, więc `useSystemModuleFormLayouts` pobiera ją zawsze, a sam
 *    `DocumentLayoutPicker` renderuje się tylko gdy po filtrze roli zostaje >1
 *    opcja (patrz C6, docs/refactor/LAYOUTY-POPRAWKI-PO-SMOKE.md).
 *  - `missingDocumentTypeId` — redirect guard: create mode bez `documentTypeId`
 *    w URL (wpisany ręcznie adres, back/forward, stara zakładka) → wraca na
 *    `listRoute`, gdzie przycisk "Utwórz dokument" otworzy DocumentTypePickerModal.
 *  - `createCompanyId` — firma wybrana w kreatorze typu dokumentu (SHARED URL
 *    CONTRACT z DocumentTypePickerModal/listą — `?documentTypeId=<id>&companyId=<uuid>`),
 *    walidowana regexem UUID (śmieciowy URL = brak firmy, jak w interceptorze `lib/api.ts`).
 *  - `effectiveDocumentTypeId` — w create znany od razu z URL (walidowany UUID),
 *    w edit/view dopiero po załadowaniu rekordu (`detailDocumentTypeId`, wartość
 *    z bazy, bez walidacji UUID).
 *  - layout-override cluster — sesyjny override layoutu formularza wybrany w
 *    `DocumentLayoutPicker` (pasek akcji nagłówka), reset przy zmianie rekordu
 *    (`recordId`). `useSystemModuleFormLayouts` dzieli cache z pickerem (ta sama
 *    queryKey) — zero dodatkowego zapytania sieciowego.
 *
 * Strona-caller zachowuje własne wywołanie `useSystemModuleActiveLayout(moduleSlug,
 * { documentTypeId: effectiveDocumentTypeId })` (feeduje też `myRoleId`/`isSuperUser`
 * do `RecursiveFormRenderer`) — hook przyjmuje jego wynik (`layoutConfig`) jako
 * `activeLayoutConfig` i tylko rozstrzyga override wokół niego.
 */
export interface UseDocumentTypeAndLayoutParamsOptions {
  isCreate: boolean;
  searchParams: URLSearchParams;
  navigate: NavigateFunction;
  // e.g. '/commercial-docs'; caller podaje ją przez `useWithMenuItem` (powrót niesie kontekst listy)
  listRoute: string;
  moduleSlug: string; // 'commercial' | 'internal' | ...
  recordId: string | null; // route id (null in create) — resets layoutOverrideId
  detailDocumentTypeId: string | null | undefined; // caller passes detailQ.data?.documentTypeId
  activeLayoutConfig: LayoutConfig | null; // caller passes layoutConfig from its own useSystemModuleActiveLayout()
}

export interface UseDocumentTypeAndLayoutParamsResult {
  createCompanyId: string | null;
  missingDocumentTypeId: boolean;
  effectiveDocumentTypeId: string | null;
  layoutOverrideId: string | null;
  setLayoutOverrideId: (id: string | null) => void;
  formLayouts: SystemModuleFormLayoutRow[] | undefined;
  overrideLayoutConfig: LayoutConfig | null;
  effectiveLayoutConfig: LayoutConfig | null;
}

export function useDocumentTypeAndLayoutParams(
  options: UseDocumentTypeAndLayoutParamsOptions,
): UseDocumentTypeAndLayoutParamsResult {
  const {
    isCreate,
    searchParams,
    navigate,
    listRoute,
    moduleSlug,
    recordId,
    detailDocumentTypeId,
    activeLayoutConfig,
  } = options;

  // Redirect guard (Part 3): create mode bez documentTypeId w URL (wpisany
  // ręcznie adres, back/forward, stary zakładka) → wracamy na listę, gdzie
  // przycisk "Utwórz dokument" otworzy DocumentTypePickerModal. Bez tego
  // ostatnia droga wyboru typu (usunięty dropdown, Part 2) zniknęłaby, a
  // backend cicho spadłby na domyślny typ, niewidoczne dla użytkownika.
  const missingDocumentTypeId = isCreate && !searchParams.get('documentTypeId');
  useEffect(() => {
    if (missingDocumentTypeId) {
      navigate(listRoute, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingDocumentTypeId]);

  // Firma wybrana w kreatorze typu dokumentu (SHARED URL CONTRACT z
  // DocumentTypePickerModal/listą — `?documentTypeId=<id>&companyId=<uuid>`).
  // Jedyne miarodajne źródło firmy dla CREATE: działa też w trybie „Wszystkie
  // firmy" (gdzie nie ma aktywnej firmy w przełączniku/localStorage). Tryb
  // edit ignoruje ten param — firma pochodzi z rekordu (`detailQ.data.companyId`),
  // header X-Company-Id nie jest tam wysyłany. Walidacja UUID (jak w interceptorze
  // lib/api.ts) — ręcznie edytowany URL (np. ?companyId=all albo śmieci) nie ma
  // trafić do backendu jako surowy header; traktujemy go jak brak firmy.
  const rawCreateCompanyId = isCreate ? searchParams.get('companyId') : null;
  const createCompanyId =
    rawCreateCompanyId && UUID_REGEX.test(rawCreateCompanyId) ? rawCreateCompanyId : null;

  // T1.6 (layouty-e1): typ dokumentu do rozstrzygnięcia layoutu — w create
  // znany od razu z URL, w edit dopiero po załadowaniu rekordu (`detailQ.data`).
  // Walidacja UUID jak przy `companyId` (backend: twarde 400
  // DOCUMENT_TYPE_ID_INVALID dla zniekształconego parametru) — śmieciowy URL
  // traktujemy jak brak typu; tryb edit bez walidacji (wartość z bazy).
  const rawCreateDocumentTypeId = isCreate ? searchParams.get('documentTypeId') : null;
  const effectiveDocumentTypeId = isCreate
    ? rawCreateDocumentTypeId && UUID_REGEX.test(rawCreateDocumentTypeId)
      ? rawCreateDocumentTypeId
      : null
    : (detailDocumentTypeId ?? null);

  // C6 (docs/refactor/LAYOUTY-POPRAWKI-PO-SMOKE.md): sesyjny override layoutu
  // formularza wybrany w DocumentLayoutPicker (pasek akcji nagłówka) — reset
  // przy zmianie rekordu. `useSystemModuleFormLayouts` dzieli cache z pickerem
  // (ta sama queryKey) — zero dodatkowego zapytania sieciowego.
  const [layoutOverrideId, setLayoutOverrideId] = useState<string | null>(null);
  useEffect(() => {
    setLayoutOverrideId(null);
  }, [recordId]);
  const { data: formLayouts } = useSystemModuleFormLayouts(moduleSlug);
  const overrideLayoutConfig = layoutOverrideId
    ? (formLayouts?.find((l) => l.id === layoutOverrideId)?.layoutConfig ?? null)
    : null;
  const effectiveLayoutConfig = overrideLayoutConfig ?? activeLayoutConfig;

  return {
    createCompanyId,
    missingDocumentTypeId,
    effectiveDocumentTypeId,
    layoutOverrideId,
    setLayoutOverrideId,
    formLayouts,
    overrideLayoutConfig,
    effectiveLayoutConfig,
  };
}
